import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
    BookOpen, Calculator, Wallet, Receipt, 
    TrendingUp, Building2, ArrowRight, CheckCircle2
} from 'lucide-react';

const ServicesPage = () => {
    const services = [
        { 
            icon: BookOpen, 
            title: 'Bookkeeping', 
            desc: 'Accurate financial records maintained monthly with cloud-based solutions.',
            features: [
                'Monthly reconciliation',
                'Cloud-based access',
                'Real-time reporting',
                'Expense tracking'
            ],
            color: 'bg-emerald-500'
        },
        { 
            icon: Calculator, 
            title: 'Tax Returns', 
            desc: 'Personal and business tax returns filed accurately and on time.',
            features: [
                'Self-assessment returns',
                'Corporation tax',
                'Tax deadline reminders',
                'HMRC correspondence'
            ],
            color: 'bg-teal-500'
        },
        { 
            icon: Wallet, 
            title: 'Payroll Services', 
            desc: 'Complete payroll management including RTI submissions to HMRC.',
            features: [
                'Monthly payroll processing',
                'RTI submissions',
                'Payslip generation',
                'Pension auto-enrollment'
            ],
            color: 'bg-cyan-500'
        },
        { 
            icon: Receipt, 
            title: 'VAT Returns', 
            desc: 'Quarterly VAT calculations and submissions handled efficiently.',
            features: [
                'Quarterly returns',
                'MTD compliance',
                'VAT scheme advice',
                'VAT registration'
            ],
            color: 'bg-green-500'
        },
        { 
            icon: TrendingUp, 
            title: 'Tax Planning', 
            desc: 'Strategic advice to minimize tax liability legally and ethically.',
            features: [
                'Tax efficiency reviews',
                'Dividend planning',
                'Capital gains advice',
                'Retirement planning'
            ],
            color: 'bg-emerald-600'
        },
        { 
            icon: Building2, 
            title: 'Company Accounts', 
            desc: 'Annual accounts preparation and Companies House filing.',
            features: [
                'Year-end accounts',
                'Companies House filing',
                'Director reports',
                'Financial statements'
            ],
            color: 'bg-teal-600'
        },
    ];

    return (
        <main data-testid="services-page">
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-hero-dark overflow-hidden">
                <div className="absolute inset-0 noise-texture" />
                <div className="absolute top-0 right-0 w-96 h-96 bg-hope-soft/20 rounded-full blur-3xl" />
                
                <div className="container-hope relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-3xl"
                    >
                        <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/90 font-medium text-sm mb-6">
                            Our Services
                        </span>
                        <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                            Premium Accounting Solutions
                        </h1>
                        <p className="text-xl text-white/70 leading-relaxed">
                            Comprehensive financial services tailored to help your UK business stay compliant and grow confidently.
                        </p>
                    </motion.div>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F1F8F4"/>
                    </svg>
                </div>
            </section>

            {/* Services Grid */}
            <section className="section-padding bg-hope-light">
                <div className="container-hope">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <motion.div
                                key={service.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                data-testid={`service-detail-${index}`}
                                className="rounded-3xl p-8 transition-all duration-500 group
                                           bg-gradient-to-br from-white via-emerald-50/70 to-emerald-100/40
                                           shadow-md hover:shadow-xl
                                           border border-emerald-100/60 hover:border-hope-emerald/30"
                            >
                                <div className={`w-16 h-16 rounded-2xl ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    <service.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="font-heading font-semibold text-2xl text-hope-forest mb-3">
                                    {service.title}
                                </h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    {service.desc}
                                </p>
                                <ul className="space-y-3">
                                    {service.features.map((feature, i) => (
                                        <li key={i} className="flex items-center gap-3 text-gray-700">
                                            <CheckCircle2 className="w-5 h-5 text-hope-soft flex-shrink-0" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section-padding bg-hero-dark relative overflow-hidden">
                <div className="absolute inset-0 noise-texture" />
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-hope-soft/20 rounded-full blur-3xl" />
                
                <div className="container-hope relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6">
                            Need a Custom Solution?
                        </h2>
                        <p className="text-white/70 text-xl mb-10 max-w-2xl mx-auto">
                            Every business is unique. Let's discuss your specific requirements.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="services-cta-btn"
                            className="btn-premium bg-white text-hope-emerald hover:bg-hope-light inline-flex items-center gap-3"
                        >
                            Get a Free Quote
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </motion.div>
                </div>
            </section>
        </main>
    );
};

export default ServicesPage;